import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Input, Label, Spinner, Table } from 'reactstrap';
import { api } from '../api/client.js';
import { useCurrentWeek } from '../hooks/useCurrentWeek.js';
import { formatKickoff } from '../utils/dates.js';

/** Read-only view of the whole season. Picking happens on the Picks page. */
export function Schedule() {
  const current = useCurrentWeek();
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [games, setGames] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.seasons().then(setSeasons).catch(() => setSeasons([]));
  }, []);

  useEffect(() => {
    if (current.season && season === null) setSeason(current.season);
  }, [current, season]);

  const load = useCallback(async () => {
    if (!season) return;
    setLoading(true);
    setError(null);

    try {
      const summaries = await api.weeks(season);
      setWeeks(summaries);

      const loaded = await Promise.all(
        summaries.map((w) => api.week(season, w.number).catch(() => null))
      );

      setGames(Object.fromEntries(
        summaries.map((w, i) => [w.number, loaded[i]?.games ?? []])
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [season]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h1 className="h3 mb-3">Season schedule</h1>

      <div className="mb-3" style={{ maxWidth: 200 }}>
        <Label for="sched-season" className="mb-1 small text-muted">Season</Label>
        <Input id="sched-season" type="select" value={season ?? ''} disabled={seasons.length === 0}
          onChange={(e) => setSeason(Number(e.target.value))}>
          {seasons.map((s) => <option key={s.year} value={s.year}>{s.year}</option>)}
        </Input>
      </div>

      {error && <Alert fade={false} color="danger">{error}</Alert>}
      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && weeks.map((week) => (
        <section key={week.number} className="mb-4">
          <h2 className="h5 d-flex align-items-center gap-2">
            Week {week.number}
            {week.isComplete && <Badge color="secondary" pill>Complete</Badge>}
          </h2>

          <Table size="sm" borderless className="mb-0">
            <tbody>
              {(games[week.number] ?? []).map((game) => (
                <tr key={game.id}>
                  <td className="text-muted small text-nowrap" style={{ width: '13rem' }}>
                    {formatKickoff(game.kickoffUtc)}
                  </td>
                  <td className="text-nowrap">
                    {game.away.displayName} <span className="text-muted">at</span> {game.home.displayName}
                  </td>
                  <td className="text-end small">
                    {game.isFinal
                      ? `${game.awayScore} - ${game.homeScore}`
                      : <span className="text-muted">{game.status}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      ))}
    </div>
  );
}
